import NProgress from "nprogress";
import "nprogress/nprogress.css";
import { setUpAutoUpdate } from "@/utils/autoUpdate";
import permission from "./permission";
import {
  createDynamicRouteManager,
  createErrorLocation,
  createRematchLocation,
  installSafeRouterPush,
} from "./routeRuntime";

/**
 * Registers the platform route runtime.
 *
 * router.js only creates the static Router instance. This module coordinates
 * permission results with the extracted dynamic-matcher runtime, while the
 * runtime itself remains responsible for component resolution and matcher
 * replacement.
 *
 * @param {VueRouter} router Created Vue Router 3 instance
 * @param {Object} options Static route and component dependencies
 */
export function installRouteGuard(router, options) {
  if (!router || typeof router.beforeEach !== "function") {
    throw new Error("installRouteGuard requires a Vue Router instance.");
  }

  const config = options || {};
  const constantRoutes = Array.isArray(config.constantRoutes)
    ? config.constantRoutes
    : [];

  if (
    typeof config.createRouter !== "function" ||
    !config.layoutComponent ||
    !config.routeViewComponent ||
    !config.notFoundComponent
  ) {
    throw new Error("installRouteGuard requires complete route configuration.");
  }

  const routeRuntime = createDynamicRouteManager(router, {
    constantRoutes: constantRoutes,
    createRouter: config.createRouter,
    layoutComponent: config.layoutComponent,
    routeViewComponent: config.routeViewComponent,
    notFoundComponent: config.notFoundComponent,
  });

  // Layout invokes this for logout and cross-platform identity changes without
  // importing router.js back into a page component.
  router.resetDynamicRoutes = function() {
    routeRuntime.resetDynamicRoutes();
    permission.resetCache();
  };

  router.beforeEach((to, from, next) => {
    const title = to && to.meta && to.meta.title ? to.meta.title : to && to.name;
    if (title) {
      document.title = title;
    }
    NProgress.start();

    if (to.path === "/Login") {
      // Login can switch identities, so reset both matcher and menu cache.
      router.resetDynamicRoutes();
      next();
      NProgress.done();
      return;
    }

    Promise.resolve()
      .then(function() {
        return permission.init(to.path, constantRoutes);
      })
      .then(function(result) {
        if (!result) {
          next();
          return;
        }

        const shouldInstallDynamicRoutes =
          result.data && !routeRuntime.isDynamicRoutesLoaded();
        if (shouldInstallDynamicRoutes) {
          routeRuntime.installDynamicRoutes(result.data);
        }

        // Direct 403/404/500 visits still initialise the menu once, so the
        // sidebar is ready when a user subsequently returns to the homepage.
        if (
          result.code === 403 ||
          result.code === 404 ||
          result.code === 500
        ) {
          next(createErrorLocation(result.code));
        } else if (result.code === 110) {
          next({ path: "/Login", replace: true });
        } else if (shouldInstallDynamicRoutes) {
          next(createRematchLocation(to));
        } else {
          next();
        }
      })
      .catch(function() {
        // permission handles API/business states; this is an unexpected guard
        // runtime failure and must not be presented as a missing page.
        next(createErrorLocation(500));
      })
      .then(function() {
        NProgress.done();
      });
  });

  router.afterEach(() => {
    NProgress.done();
  });
  setUpAutoUpdate(router);
  installSafeRouterPush(router);

  return {
    resetDynamicRoutes: router.resetDynamicRoutes,
  };
}

export default installRouteGuard;
